import type { LinkData } from './LinkItem.tsx';

export type ApplyTopLinksPinToggleResult = {
	nextLinks: LinkData[];
	/** True when pin/unpin changed list order (run layout animation). */
	didReorder: boolean;
};

/**
 * Toggles pin on a link.
 * - Pinning: move after the last currently pinned item (left / pinned group in LTR).
 * - Unpinning: if another pinned item appears later in the list, move this row immediately after
 *   the last remaining pinned item (start of unpinned group). If this is the last pinned item
 *   (nothing pinned after it), clear pin in place.
 */
export function applyTopLinksPinToggle(
	links: LinkData[],
	linkId: string,
): ApplyTopLinksPinToggleResult {
	const idx = links.findIndex((l) => l.id === linkId);
	if (idx === -1) {
		return { nextLinks: links, didReorder: false };
	}
	const link = links[idx];

	// When unpinning the last pinned item (nothing pinned after it), toggle in place.
	if (link.isPinned && !links.slice(idx + 1).some((l) => l.isPinned)) {
		return {
			nextLinks: links.map((l, i) => (i === idx ? { ...l, isPinned: false } : l)),
			didReorder: false,
		};
	}

	// Both pin and unpin place the link right after the last pinned item.
	const newPinned = !link.isPinned;
	const without = links.filter((_, i) => i !== idx);
	let insertAt = 0;
	for (let i = without.length - 1; i >= 0; i--) {
		if (without[i].isPinned) {
			insertAt = i + 1;
			break;
		}
	}
	const updatedLink = { ...link, isPinned: newPinned };
	const nextLinks = [...without.slice(0, insertAt), updatedLink, ...without.slice(insertAt)];
	return { nextLinks, didReorder: insertAt !== idx };
}

/**
 * Inserts a new pinned link immediately after the last currently pinned item (same slot as pinning via
 * {@link applyTopLinksPinToggle}). If no items are pinned, inserts at index 0.
 */
export function insertNewPinnedTopLink(
	links: LinkData[],
	newLink: LinkData,
	maxCount: number,
): LinkData[] {
	let insertAt = 0;
	for (let i = links.length - 1; i >= 0; i--) {
		if (links[i].isPinned) {
			insertAt = i + 1;
			break;
		}
	}
	return [...links.slice(0, insertAt), newLink, ...links.slice(insertAt)].slice(0, maxCount);
}
